import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Search,
  Plus,
  Send,
  Loader2,
  Inbox as InboxIcon,
  MessageSquare,
  Mail,
  Star,
  Paperclip,
  Image as ImageIcon,
  Smile,
  X,
  ArrowDownUp,
} from "lucide-react";
import { toast } from "sonner";
import { inboxApi, customersApi } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { colorFor, initialsFor } from "../../components/chat/ChatAvatar";

/**
 * Mobile Inbox — single-column, large-tap-target redesign of the desktop
 * customer-facing inbox (SMS + Email conversations).
 *
 *  List view  (default)    — search + sort + rows with big avatars, channel
 *                            chips, unread pills, star toggle
 *  Thread view (?c=<id>)   — full-screen z-[60] chat, dark navy header, no
 *                            bubbles, day dividers, composer with photo /
 *                            file / emoji / send icons
 *
 * State is URL-driven so the bottom-nav "Inbox" tap always returns to the
 * list. Feature parity with mobile chat pass: text + attachments +
 * emoji-picker. Reactions/threads intentionally omitted (customer inbox
 * doesn't have those).
 */

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const t = new Date();
  const same = d.toDateString() === t.toDateString();
  if (same) return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const y = new Date(t.getTime() - 86400000);
  if (d.toDateString() === y.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}
function dayLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const t = new Date();
  const y = new Date(t.getTime() - 86400000);
  const same = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (same(d, t)) return "TODAY";
  if (same(d, y)) return "YESTERDAY";
  return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" }).toUpperCase();
}
function timeLabel(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export default function MobileInbox() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [composeOpen, setComposeOpen] = useState(false);

  const activeId = searchParams.get("c") || null;

  const loadConvs = useCallback(async () => {
    try {
      const rows = await inboxApi.conversations();
      setConversations(rows || []);
    } catch { setConversations([]); }
  }, []);

  useEffect(() => {
    setLoading(true);
    loadConvs().finally(() => setLoading(false));
  }, [loadConvs]);

  // Poll every 8s so the list stays fresh even when the thread is idle.
  useEffect(() => {
    const id = setInterval(() => { loadConvs(); }, 8000);
    return () => clearInterval(id);
  }, [loadConvs]);

  const open = (id) => setSearchParams((p) => { const n = new URLSearchParams(p); n.set("c", id); return n; }, { replace: false });
  const back = () => setSearchParams((p) => { const n = new URLSearchParams(p); n.delete("c"); return n; }, { replace: false });

  const totalUnread = conversations.reduce((s, c) => s + (c.unread || 0), 0);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = conversations.filter((c) => !q ||
      (c.contactName || "").toLowerCase().includes(q) ||
      (c.contactPhone || "").includes(search) ||
      (c.contactEmail || "").toLowerCase().includes(q)
    );
    const byNewest = (a, b) => (b.lastMessageAt || "").localeCompare(a.lastMessageAt || "");
    const sorted = [...rows];
    if (sortBy === "oldest") sorted.sort((a, b) => (a.lastMessageAt || "").localeCompare(b.lastMessageAt || ""));
    else if (sortBy === "unread") sorted.sort((a, b) => Number(!!b.unread) - Number(!!a.unread) || byNewest(a, b));
    else if (sortBy === "important") sorted.sort((a, b) => Number(!!b.important) - Number(!!a.important) || byNewest(a, b));
    else sorted.sort(byNewest);
    return sorted;
  }, [conversations, search, sortBy]);

  const toggleStar = useCallback(async (conv, e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    const next = !conv.important;
    setConversations((prev) => prev.map((c) => c.id === conv.id ? { ...c, important: next } : c));
    try {
      await inboxApi.star(conv.id, next);
    } catch {
      setConversations((prev) => prev.map((c) => c.id === conv.id ? { ...c, important: conv.important } : c));
      toast.error("Could not update star");
    }
  }, []);

  if (activeId) {
    const conv = conversations.find((c) => c.id === activeId);
    return (
      <MobileInboxThread
        conv={conv}
        conversationId={activeId}
        meRole={user?.role}
        onBack={back}
        onSent={() => { loadConvs(); }}
      />
    );
  }

  return (
    <div
      className="mv md:hidden min-h-screen w-full"
      style={{
        color: "#0f172a",
        paddingTop: "calc(3.5rem + env(safe-area-inset-top))",
        paddingBottom: "calc(5rem + env(safe-area-inset-bottom))",
      }}
      data-testid="mobile-inbox"
    >
      {/* Search + new + sort */}
      <div className="px-4 pt-3 pb-2 flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2" color="#64748b" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, phone, email"
            data-testid="mobile-inbox-search"
            className="w-full h-11 pl-9 pr-3 rounded-full text-[15px] border"
            style={{ backgroundColor: "#ffffff", borderColor: "#e2e8f0", color: "#0f172a" }}
          />
        </div>
        <button
          onClick={() => setComposeOpen(true)}
          data-testid="mobile-inbox-new"
          aria-label="New message"
          className="relative h-11 w-11 rounded-full flex items-center justify-center active:opacity-90"
          style={{ backgroundColor: "#2563EB" }}
        >
          <Plus className="h-5 w-5" color="#ffffff" strokeWidth={2.5} style={{ color: "#ffffff", stroke: "#ffffff" }} />
          {totalUnread > 0 && (
            <span
              data-testid="mobile-inbox-total-unread"
              className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold flex items-center justify-center"
              style={{ backgroundColor: "#F43F5E", color: "#ffffff", border: "2px solid #ffffff" }}
            >
              {totalUnread > 99 ? "99+" : totalUnread}
            </span>
          )}
        </button>
      </div>

      <div className="px-4 pb-2 flex items-center gap-2">
        <ArrowDownUp className="h-3.5 w-3.5" color="#64748b" style={{ color: "#64748b", stroke: "#64748b" }} />
        <label className="text-[11px] font-bold uppercase tracking-wide" style={{ color: "#64748b" }}>Sort</label>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          data-testid="mobile-inbox-sort"
          className="flex-1 h-9 rounded-md border px-2 text-[13.5px]"
          style={{ backgroundColor: "#ffffff", borderColor: "#e2e8f0", color: "#0f172a" }}
        >
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="unread">Unread first</option>
          <option value="important">Important first</option>
        </select>
      </div>

      {loading ? (
        <div className="mt-6 flex items-center justify-center text-[14px]" style={{ color: "#64748b" }}>
          <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="px-6 pt-16 text-center">
          <InboxIcon className="h-10 w-10 mx-auto mb-3" color="#94a3b8" style={{ color: "#94a3b8", stroke: "#94a3b8" }} />
          <div className="text-[15px] font-semibold" style={{ color: "#0f172a" }}>No conversations yet</div>
          <div className="text-[13px] mt-1" style={{ color: "#64748b" }}>
            Tap <span className="font-semibold" style={{ color: "#334155" }}>+</span> to message a customer.
          </div>
        </div>
      ) : (
        <ul className="mt-1" style={{ backgroundColor: "#ffffff", borderTop: "1px solid #e2e8f0", borderBottom: "1px solid #e2e8f0" }} data-testid="mobile-inbox-list">
          {filtered.map((c) => {
            const ChannelIcon = c.lastChannel === "email" ? Mail : MessageSquare;
            return (
              <li key={c.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <button
                  onClick={() => open(c.id)}
                  data-testid={`mobile-inbox-row-${c.id}`}
                  className="w-full text-left px-4 py-3 flex items-center gap-3 active:bg-slate-50"
                >
                  <div
                    className="h-12 w-12 rounded-xl flex items-center justify-center font-bold text-white shrink-0"
                    style={{ background: c.isCurrentUser ? "#10B981" : colorFor(c.contactName || ""), fontSize: 17 }}
                    title={c.isCurrentUser ? "This sender is one of your users" : undefined}
                  >
                    {initialsFor(c.contactName || "?")}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className={`text-[15.5px] truncate ${c.unread ? "font-bold" : "font-semibold"}`} style={{ color: "#0f172a" }}>
                        {c.contactName || "(no name)"}
                      </div>
                      <span
                        onClick={(e) => toggleStar(c, e)}
                        data-testid={`mobile-inbox-star-${c.id}`}
                        role="button"
                        aria-label="Star conversation"
                        className="ml-auto shrink-0 h-6 w-6 rounded-full flex items-center justify-center active:bg-slate-100"
                      >
                        <Star
                          className="h-[18px] w-[18px]"
                          color={c.important ? "#F59E0B" : "#cbd5e1"}
                          fill={c.important ? "#F59E0B" : "none"}
                          strokeWidth={2}
                          style={{ color: c.important ? "#F59E0B" : "#cbd5e1", stroke: c.important ? "#F59E0B" : "#cbd5e1" }}
                        />
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <ChannelIcon className="h-3.5 w-3.5 shrink-0" color="#64748b" style={{ color: "#64748b", stroke: "#64748b" }} />
                      <div className="text-[12.5px] truncate flex-1" style={{ color: c.unread ? "#0f172a" : "#64748b" }}>
                        {c.lastMessagePreview || c.lastMessage?.body || "No messages yet"}
                      </div>
                      <div className="text-[11px] shrink-0" style={{ color: "#94a3b8" }}>{relTime(c.lastMessageAt)}</div>
                    </div>
                  </div>
                  {c.unread > 0 && (
                    <span className="ml-1 min-w-[22px] h-6 px-1.5 rounded-full flex items-center justify-center text-[11px] font-bold"
                          style={{ backgroundColor: "#2563EB", color: "#ffffff" }}
                          data-testid={`mobile-inbox-unread-${c.id}`}>
                      {c.unread > 99 ? "99+" : c.unread}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {composeOpen && (
        <MobileComposeModal
          onClose={() => setComposeOpen(false)}
          onComposed={async ({ conversationId }) => {
            setComposeOpen(false);
            await loadConvs();
            if (conversationId) open(conversationId);
            toast.success("Message sent");
          }}
        />
      )}
    </div>
  );
}

// ---------------- Thread view ----------------

function MobileInboxThread({ conv, conversationId, onBack, onSent }) {
  const [detail, setDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [channel, setChannel] = useState(conv?.lastChannel || "sms");
  const [showEmoji, setShowEmoji] = useState(false);
  const [subject, setSubject] = useState("");
  const scrollRef = useRef(null);
  const lastTsRef = useRef(null);
  const taRef = useRef(null);
  const imgRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [det, msgs] = await Promise.all([
        inboxApi.getConversation(conversationId),
        inboxApi.messages(conversationId, { limit: 300 }),
      ]);
      setDetail(det);
      setMessages(msgs || []);
      setChannel((det?.lastChannel) || conv?.lastChannel || "sms");
      lastTsRef.current = msgs && msgs.length ? msgs[msgs.length - 1].createdAt : null;
      await inboxApi.markRead(conversationId).catch(() => {});
    } finally {
      setLoading(false);
    }
  }, [conversationId, conv]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  // Poll for new messages every 3s.
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const after = lastTsRef.current;
        const fresh = await inboxApi.messages(conversationId, after ? { after } : { limit: 300 });
        if (fresh.length) {
          setMessages((prev) => {
            const seen = new Set(prev.map((m) => m.id));
            const add = fresh.filter((m) => !seen.has(m.id));
            return add.length ? [...prev, ...add] : prev;
          });
          lastTsRef.current = fresh[fresh.length - 1].createdAt;
          inboxApi.markRead(conversationId).catch(() => {});
        }
      } catch { /* ignore */ }
    }, 3000);
    return () => clearInterval(id);
  }, [conversationId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const stageFiles = async (files) => {
    setUploading(true);
    try {
      for (const f of files) {
        const a = await inboxApi.upload(f);
        setAttachments((prev) => [...prev, a]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const send = async () => {
    const body = text.trim();
    if ((!body && !attachments.length) || sending) return;
    setSending(true);
    try {
      const payload = { channel, body, mediaUrls: attachments.map((a) => a.url) };
      if (channel === "email" && subject.trim()) payload.subject = subject.trim();
      const msg = await inboxApi.send(conversationId, payload);
      setMessages((prev) => [...prev, msg]);
      lastTsRef.current = msg.createdAt;
      setText(""); setSubject(""); setAttachments([]);
      onSent && onSent(msg);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send");
    } finally {
      setSending(false);
    }
  };

  const contactName = detail?.contactName || conv?.contactName || "Conversation";
  const contactMeta = channel === "email"
    ? (detail?.contactEmail || conv?.contactEmail || "")
    : (detail?.contactPhone || conv?.contactPhone || "");

  // Group messages by day.
  const rows = [];
  let lastDay = null;
  messages.forEach((m) => {
    const d = dayLabel(m.createdAt);
    if (d !== lastDay) { rows.push({ divider: d, key: `d-${m.id}` }); lastDay = d; }
    rows.push({ msg: m, key: m.id });
  });

  return (
    <div
      className="mv md:hidden fixed inset-0 z-[60] flex flex-col"
      style={{ backgroundColor: "#F1F5F9" }}
      data-testid="mobile-inbox-thread"
    >
      {/* Dark navy header */}
      <div
        className="shrink-0 flex items-center px-2 gap-2"
        style={{
          backgroundColor: "#0B1E3F",
          height: "3.75rem",
          paddingTop: "env(safe-area-inset-top)",
        }}
      >
        <button
          onClick={onBack}
          data-testid="mobile-inbox-back"
          className="h-10 w-10 rounded-full flex items-center justify-center active:bg-white/10"
          aria-label="Back"
        >
          <ArrowLeft className="h-6 w-6" color="#ffffff" style={{ color: "#ffffff", stroke: "#ffffff" }} />
        </button>
        <div className="h-10 w-10 rounded-lg shrink-0 flex items-center justify-center font-bold" style={{ background: colorFor(contactName), color: "#ffffff", fontSize: 15 }}>
          {initialsFor(contactName)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[16px] font-bold truncate" style={{ color: "#ffffff" }}>{contactName}</div>
          <div className="text-[11.5px] truncate flex items-center gap-1" style={{ color: "#cbd5e1" }}>
            {channel === "email"
              ? <Mail className="h-3 w-3" color="#93c5fd" style={{ color: "#93c5fd", stroke: "#93c5fd" }} />
              : <MessageSquare className="h-3 w-3" color="#93c5fd" style={{ color: "#93c5fd", stroke: "#93c5fd" }} />
            }
            {contactMeta || (channel === "email" ? "no email on file" : "no phone on file")}
          </div>
        </div>
      </div>

      {/* Channel picker (SMS ↔ Email) */}
      <div className="shrink-0 flex items-center gap-1 px-3 pt-2" style={{ backgroundColor: "#F1F5F9" }}>
        <button
          onClick={() => setChannel("sms")}
          data-testid="mobile-inbox-channel-sms"
          className="h-8 px-3 rounded-full inline-flex items-center gap-1.5 text-[12.5px] font-semibold"
          style={{ backgroundColor: channel === "sms" ? "#2563EB" : "#ffffff", color: channel === "sms" ? "#ffffff" : "#334155", border: "1px solid #e2e8f0" }}
        >
          <MessageSquare className="h-3.5 w-3.5" color={channel === "sms" ? "#ffffff" : "#334155"} style={{ color: channel === "sms" ? "#ffffff" : "#334155", stroke: channel === "sms" ? "#ffffff" : "#334155" }} /> SMS
        </button>
        <button
          onClick={() => setChannel("email")}
          data-testid="mobile-inbox-channel-email"
          className="h-8 px-3 rounded-full inline-flex items-center gap-1.5 text-[12.5px] font-semibold"
          style={{ backgroundColor: channel === "email" ? "#2563EB" : "#ffffff", color: channel === "email" ? "#ffffff" : "#334155", border: "1px solid #e2e8f0" }}
        >
          <Mail className="h-3.5 w-3.5" color={channel === "email" ? "#ffffff" : "#334155"} style={{ color: channel === "email" ? "#ffffff" : "#334155", stroke: channel === "email" ? "#ffffff" : "#334155" }} /> Email
        </button>
      </div>

      {/* Message stream */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto py-2 mt-1" data-testid="mobile-inbox-stream">
        {loading && messages.length === 0 && (
          <div className="text-center py-10" style={{ color: "#94a3b8" }}>
            <Loader2 className="h-5 w-5 mx-auto animate-spin mb-2" /> Loading…
          </div>
        )}
        {!loading && messages.length === 0 && (
          <div className="text-center py-10 text-[13px]" style={{ color: "#94a3b8" }}>
            No messages yet — say hello 👋
          </div>
        )}
        {rows.map((r) => {
          if (r.divider) {
            return (
              <div key={r.key} className="flex items-center gap-3 my-3 px-3">
                <div className="flex-1 h-px" style={{ backgroundColor: "#cbd5e1" }} />
                <span className="text-[10.5px] font-semibold uppercase tracking-wider" style={{ color: "#64748b" }}>{r.divider}</span>
                <div className="flex-1 h-px" style={{ backgroundColor: "#cbd5e1" }} />
              </div>
            );
          }
          return <InboxMessageRow key={r.key} m={r.msg} contactName={contactName} />;
        })}
      </div>

      {/* Composer */}
      <div
        className="shrink-0 px-3 pt-2"
        style={{
          backgroundColor: "#F1F5F9",
          paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))",
        }}
      >
        {/* Staged attachments */}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {attachments.map((a, i) => (
              <div key={a.url + i} className="flex items-center gap-1.5 h-7 pl-2 pr-1 rounded-full text-[12px]" style={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0", color: "#475569" }}>
                {a.kind === "image" || (a.contentType || "").startsWith("image/") ? <ImageIcon className="h-3.5 w-3.5" /> : <Paperclip className="h-3.5 w-3.5" />}
                <span className="truncate max-w-[140px]">{a.filename || a.name}</span>
                <button onClick={() => setAttachments((p) => p.filter((_, idx) => idx !== i))} className="h-5 w-5 rounded-full flex items-center justify-center active:bg-slate-100" aria-label="Remove attachment">
                  <X className="h-3.5 w-3.5" color="#64748b" style={{ color: "#64748b", stroke: "#64748b" }} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Email subject line */}
        {channel === "email" && (
          <div className="mb-2">
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject (optional)"
              data-testid="mobile-inbox-subject"
              className="w-full h-10 px-3 rounded-lg text-[14px]"
              style={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0", color: "#0f172a" }}
            />
          </div>
        )}

        <div className="relative flex items-end gap-1.5 rounded-2xl px-2 py-1.5" style={{ backgroundColor: "#0B1E3F1A" }}>
          {showEmoji && (
            <div className="absolute bottom-full left-2 mb-2 w-64 grid grid-cols-8 gap-0.5 rounded-xl p-2 z-30" style={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0", boxShadow: "0 6px 24px rgba(15,23,42,0.15)" }}>
              {["👍","❤️","😂","🎉","🙌","🔥","👀","✅","🚀","😊","😍","🤔","😅","💯","👋","☕","🍕","🙏","👏","💪","😎","🤝","📞","📅","🛠️","🧰","🚚","🏠","💰","⚡","🌟","🎯"].map((e) => (
                <button
                  key={e}
                  onClick={() => { setText((t) => t + e); setShowEmoji(false); taRef.current?.focus(); }}
                  className="h-8 w-8 rounded active:bg-slate-100 text-[17px]"
                >
                  {e}
                </button>
              ))}
            </div>
          )}

          {/* Action icons */}
          <div className="flex items-center gap-1.5 pb-0.5">
            <button
              onClick={() => imgRef.current?.click()}
              data-testid="mobile-inbox-image-btn"
              aria-label="Upload image"
              className="h-11 w-11 rounded-lg flex items-center justify-center shadow-sm active:opacity-90"
              style={{ backgroundColor: "#0EA5E9" }}
            >
              <ImageIcon className="h-6 w-6 shrink-0" color="#ffffff" strokeWidth={2.5} style={{ color: "#ffffff", stroke: "#ffffff" }} />
            </button>
            <button
              onClick={() => fileRef.current?.click()}
              data-testid="mobile-inbox-file-btn"
              aria-label="Attach file"
              className="h-11 w-11 rounded-lg flex items-center justify-center shadow-sm active:opacity-90"
              style={{ backgroundColor: "#10B981" }}
            >
              <Paperclip className="h-6 w-6 shrink-0" color="#ffffff" strokeWidth={2.5} style={{ color: "#ffffff", stroke: "#ffffff" }} />
            </button>
            <button
              onClick={() => setShowEmoji((v) => !v)}
              data-testid="mobile-inbox-emoji-btn"
              aria-label="Emoji"
              className="h-11 w-11 rounded-lg flex items-center justify-center shadow-sm active:opacity-90"
              style={{ backgroundColor: "#F59E0B" }}
            >
              <Smile className="h-6 w-6 shrink-0" color="#ffffff" strokeWidth={2.5} style={{ color: "#ffffff", stroke: "#ffffff" }} />
            </button>
          </div>

          <textarea
            ref={taRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={1}
            placeholder={channel === "email" ? "Write an email…" : "Text message…"}
            data-testid="mobile-inbox-input"
            className="flex-1 resize-none bg-transparent px-2 py-1.5 text-[15px] max-h-32 min-h-[36px] focus:outline-none"
            style={{ color: "#0f172a" }}
          />

          <div className="pb-0.5">
            <button
              onClick={send}
              disabled={uploading || sending || (!text.trim() && !attachments.length)}
              data-testid="mobile-inbox-send"
              aria-label="Send"
              className="h-11 w-11 rounded-lg flex items-center justify-center shadow-sm disabled:opacity-50 active:opacity-90"
              style={{ backgroundColor: "#2563EB", color: "#ffffff" }}
            >
              {sending
                ? <Loader2 className="h-6 w-6 animate-spin" color="#ffffff" style={{ color: "#ffffff", stroke: "#ffffff" }} />
                : <Send className="h-6 w-6" color="#ffffff" strokeWidth={2.5} style={{ color: "#ffffff", stroke: "#ffffff" }} />
              }
            </button>
          </div>

          <input ref={imgRef} type="file" accept="image/*" hidden onChange={(e) => { const f = Array.from(e.target.files || []); e.target.value = ""; if (f.length) stageFiles(f); }} />
          <input ref={fileRef} type="file" accept="application/pdf,.doc,.docx,.csv,.txt,image/*" hidden onChange={(e) => { const f = Array.from(e.target.files || []); e.target.value = ""; if (f.length) stageFiles(f); }} />
        </div>
        {uploading && <div className="text-[11px] mt-1" style={{ color: "#64748b" }}>Uploading…</div>}
      </div>
    </div>
  );
}

function InboxMessageRow({ m, contactName }) {
  const outbound = m.direction === "outbound" || m.from === "us";
  const senderName = outbound ? (m.senderName || "You") : (m.contactName || contactName || "Customer");
  return (
    <div className="flex gap-3 mt-3 px-3" data-testid={`mobile-inbox-msg-${m.id}`}>
      <div
        className="rounded-xl flex items-center justify-center font-bold text-white shrink-0"
        style={{ width: 44, height: 44, background: outbound ? "#2563EB" : colorFor(senderName), fontSize: 16 }}
      >
        {initialsFor(senderName)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-[15px] font-bold" style={{ color: "#0f172a" }}>{senderName}</span>
          <span className="text-[11px]" style={{ color: "#64748b" }}>{timeLabel(m.createdAt)}</span>
          {m.channel && (
            <span className="text-[10.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ backgroundColor: "#DBEAFE", color: "#1E40AF" }}>
              {m.channel}
            </span>
          )}
          {m.status && m.status !== "delivered" && (
            <span className="text-[10.5px] uppercase tracking-wider" style={{ color: m.status === "failed" ? "#DC2626" : "#94a3b8" }}>
              {m.status}
            </span>
          )}
        </div>
        {m.subject && <div className="text-[13.5px] font-semibold mt-0.5" style={{ color: "#334155" }}>{m.subject}</div>}
        {m.body && (
          <div className="text-[15px] leading-[1.45] whitespace-pre-wrap break-words mt-0.5" style={{ color: "#0f172a" }}>
            {m.body}
          </div>
        )}
        {(m.mediaUrls || []).length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-2">
            {(m.mediaUrls || []).map((url, i) => (
              <a key={i} href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[12px]" style={{ backgroundColor: "#0B1E3F", color: "#ffffff" }}>
                <Paperclip className="h-3.5 w-3.5" color="#93c5fd" style={{ color: "#93c5fd", stroke: "#93c5fd" }} />
                Attachment {i + 1}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------- Compose modal ----------------

function MobileComposeModal({ onClose, onComposed }) {
  const [channel, setChannel] = useState("sms");
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(null);
  const [manual, setManual] = useState("");
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    customersApi.list().then(setCustomers).catch(() => setCustomers([]));
  }, []);

  const results = useMemo(() => {
    if (!q.trim()) return [];
    const s = q.toLowerCase();
    return customers.filter((c) =>
      (c.name || "").toLowerCase().includes(s) ||
      (c.email || "").toLowerCase().includes(s) ||
      (c.phone || "").includes(q)
    ).slice(0, 6);
  }, [q, customers]);

  const submit = async () => {
    if (sending) return;
    if (!body.trim()) return;
    if (!picked && !manual.trim()) { toast.error("Choose a customer or enter a recipient"); return; }
    const payload = { channel, body: body.trim(), subject: subject.trim() || undefined };
    if (picked) payload.customerId = picked.id;
    else { payload.to = manual.trim(); payload.name = name.trim() || undefined; }
    setSending(true);
    try {
      const res = await inboxApi.compose(payload);
      onComposed(res);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send");
    } finally { setSending(false); }
  };

  return (
    <div
      className="mv fixed inset-0 z-[70] flex items-end sm:items-center sm:justify-center"
      style={{ backgroundColor: "rgba(15,23,42,0.55)" }}
      onClick={onClose}
      data-testid="mobile-inbox-compose"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden"
        style={{ backgroundColor: "#ffffff", color: "#0f172a" }}
      >
        <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: "1px solid #e2e8f0" }}>
          <div className="text-[16px] font-bold" style={{ color: "#0B1E3F" }}>New message</div>
          <button
            onClick={onClose}
            data-testid="mobile-inbox-compose-close"
            className="h-8 w-8 rounded-full flex items-center justify-center active:bg-slate-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" color="#475569" style={{ color: "#475569", stroke: "#475569" }} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3 max-h-[75vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setChannel("sms")}
              data-testid="mobile-compose-sms"
              className="h-11 rounded-full text-[13.5px] font-semibold flex items-center justify-center gap-1.5"
              style={{ backgroundColor: channel === "sms" ? "#2563EB" : "#F1F5F9", color: channel === "sms" ? "#ffffff" : "#334155" }}
            >
              <MessageSquare className="h-4 w-4" color={channel === "sms" ? "#ffffff" : "#334155"} style={{ color: channel === "sms" ? "#ffffff" : "#334155", stroke: channel === "sms" ? "#ffffff" : "#334155" }} /> SMS
            </button>
            <button
              onClick={() => setChannel("email")}
              data-testid="mobile-compose-email"
              className="h-11 rounded-full text-[13.5px] font-semibold flex items-center justify-center gap-1.5"
              style={{ backgroundColor: channel === "email" ? "#2563EB" : "#F1F5F9", color: channel === "email" ? "#ffffff" : "#334155" }}
            >
              <Mail className="h-4 w-4" color={channel === "email" ? "#ffffff" : "#334155"} style={{ color: channel === "email" ? "#ffffff" : "#334155", stroke: channel === "email" ? "#ffffff" : "#334155" }} /> Email
            </button>
          </div>

          {picked ? (
            <div className="flex items-center gap-2.5 p-2.5 rounded-lg" style={{ backgroundColor: "#EFF6FF", border: "1px solid #BFDBFE" }} data-testid="mobile-compose-picked">
              <span
                className="h-9 w-9 rounded-full flex items-center justify-center text-[13px] font-bold text-white"
                style={{ background: colorFor(picked.name || "?") }}
              >{initialsFor(picked.name || "?")}</span>
              <div className="min-w-0 flex-1">
                <div className="text-[14px] font-semibold truncate" style={{ color: "#0f172a" }}>{picked.name}</div>
                <div className="text-[12px] truncate" style={{ color: "#64748b" }}>
                  {channel === "sms" ? picked.phone || "No phone" : picked.email || "No email"}
                </div>
              </div>
              <button onClick={() => setPicked(null)} className="text-[13px] font-semibold" style={{ color: "#2563EB" }}>Change</button>
            </div>
          ) : (
            <>
              <div>
                <div className="text-[11.5px] font-bold uppercase tracking-wide mb-1" style={{ color: "#64748b" }}>Find a customer</div>
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2" color="#64748b" style={{ color: "#64748b", stroke: "#64748b" }} />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search by name, phone or email"
                    data-testid="mobile-compose-search"
                    className="w-full h-11 pl-9 pr-3 rounded-lg text-[14.5px]"
                    style={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0", color: "#0f172a" }}
                  />
                </div>
                {results.length > 0 && (
                  <div className="mt-1 rounded-lg overflow-hidden" style={{ border: "1px solid #e2e8f0" }}>
                    {results.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => { setPicked(c); setQ(""); }}
                        data-testid={`mobile-compose-pick-${c.id}`}
                        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left active:bg-slate-50"
                        style={{ borderBottom: "1px solid #f1f5f9" }}
                      >
                        <span
                          className="h-8 w-8 rounded-full flex items-center justify-center text-[11.5px] font-bold text-white"
                          style={{ background: colorFor(c.name || "?") }}
                        >{initialsFor(c.name || "?")}</span>
                        <span className="min-w-0">
                          <span className="block text-[13.5px] font-semibold truncate" style={{ color: "#0f172a" }}>{c.name}</span>
                          <span className="block text-[12px] truncate" style={{ color: "#64748b" }}>{channel === "sms" ? c.phone : c.email}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 text-[11.5px]" style={{ color: "#64748b" }}>
                <div className="flex-1 h-px" style={{ backgroundColor: "#e2e8f0" }} /> or enter manually
                <div className="flex-1 h-px" style={{ backgroundColor: "#e2e8f0" }} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input
                  value={manual}
                  onChange={(e) => setManual(e.target.value)}
                  placeholder={channel === "sms" ? "+15551234567" : "name@email.com"}
                  data-testid="mobile-compose-manual"
                  className="h-11 px-3 rounded-lg text-[14.5px]"
                  style={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0", color: "#0f172a" }}
                />
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name (optional)"
                  className="h-11 px-3 rounded-lg text-[14.5px]"
                  style={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0", color: "#0f172a" }}
                />
              </div>
            </>
          )}

          {channel === "email" && (
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
              data-testid="mobile-compose-subject"
              className="w-full h-11 px-3 rounded-lg text-[14.5px]"
              style={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0", color: "#0f172a" }}
            />
          )}

          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={channel === "sms" ? "Text message…" : "Write your email…"}
            data-testid="mobile-compose-body"
            rows={5}
            className="w-full px-3 py-2 rounded-lg text-[14.5px] resize-y"
            style={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0", color: "#0f172a" }}
          />

          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={onClose}
              data-testid="mobile-compose-cancel"
              className="flex-1 h-12 rounded-lg text-[14.5px] font-semibold"
              style={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0", color: "#475569" }}
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={sending || (!body.trim())}
              data-testid="mobile-compose-send"
              className="flex-[2] h-12 rounded-lg text-[14.5px] font-bold disabled:opacity-60 flex items-center justify-center gap-1.5"
              style={{ backgroundColor: "#2563EB", color: "#ffffff" }}
            >
              {sending
                ? <Loader2 className="h-5 w-5 animate-spin" color="#ffffff" style={{ color: "#ffffff", stroke: "#ffffff" }} />
                : <Send className="h-5 w-5" color="#ffffff" style={{ color: "#ffffff", stroke: "#ffffff" }} />
              }
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
